import { WorkerSchema } from "types/worker";
import { MULTI_STATUS } from "utils/global/constants";
import { Attachment } from "./attachment";
import { City } from "./city";
import { Gender } from "./customer";
import { Invoice } from "./invoice";

export type AdditionalInstallationInformation = {
  // installation details
  additionalPurpose: string;
  additionalStatus: string;
  additionalTimeFrom: string;
  additionalTimeTill: string;
  additionalLengthOfBoard: string;
  additionalStreetAndHouseNumber: string;
  additionalZipCode: string;
  additionalCity: string;
  additionalIsTwoSiteBlock: string;
  additionalIsWithPermission: string;
  additionalWithOutsideLift: string;

  // contact details
  additionalSalutation: string;
  additionalFirstName: string;
  additionalLastName: string;
  additionalEmail: string;
  additionalCompanyName: string;
  additionalContactStreetAndHouseNumber: string;
  additionalContactCityAndZipCode: string;
  additionalNotes: string;
  additionalTelephoneNumber: string;

  commentHVZ: string;
  additionalSetupDoneDateTime?: string;
};

export type BarrierDetailsSchema = {
  // Address
  halteverbot24: string;
  city: string;
};

export type LicensePlates = {
  licensePlate1: string;
  licensePlate2: string;
  licensePlate3: string;
  licensePlate4: string;
  licensePlate5: string;
  licensePlate6: string;
  licensePlate7: string;
  licensePlate8: string;
  licensePlate9: string;
  licensePlate10: string;
};

export enum Purpose {
  MOVE = "umzug",
  CONSTRUCTION_SITE = "baustelle",
  OTHER = "sonstiges",
  DELIVERY = "lieferung",
  FILM_AND_EVENTS = "film&events",
}

export type InstallationSchema = {
  id: string;
  createdAt: string;
  updatedAt: string;
  parentDomain: string;
  street?: string;
  houseNumber?: string;
  streetAndHouseNumber: string;
  zipCode: string | null;
  dateRangeFrom: string;
  dateRangeTo: string;
  timeFrom: string;
  timeTill: string;
  lengthOfBoard: string;
  isTwoSiteBlock: boolean;
  isWithPermission: boolean;
  withOutsideLift: boolean;
  purpose: Purpose;
  status: string;
  statusRemarks: any;
  lexwareData: LexwareInvoiceType | null;
  city: City;
  customer: Customer;
  payment: Payment;
  additionalInformation: Partial<AdditionalInstallationInformation> | null;
  licensePlates: Partial<LicensePlates> | null;
  priceTable: PriceTableSchema[] | null;
  updatedPrice: UpdatedPrice | null;
  barrierDetails: BarrierDetailsSchema | null;
  noSignInstallation: boolean;
  isImportant: boolean;
  setupDate: string;
  setupTime: string;
  pickupDate: string;
  updatedDateRangeFrom: string;
  updatedDateRangeTo: string;
  invoiceUploadedByClient: boolean;
  invoicePaidByClient: boolean;
  taxBooking: boolean;
  assignedTo: WorkerSchema | null;
  pickupAssignedTo: WorkerSchema | null;
  workerOrderNumber: number | null;
  workerPickupOrderNumber: number | null;
  setupEventName: string;
  pickupEventName: string;
  setupDoneDateTime: string | null;
  pickupDoneDateTime: string | null;
  notice: string;
  workerNotice: string;
  paymentNotice: string;
  workerInstructionNumber: number | null;
  publicAttachments: Attachment[] | null;
  parkedCar: ParkedCars[];
  invoice: Invoice | null;
} & InstallationStatus &
  InstallationStatusDateTime;

export type InstallationStatus = {
  acceptOrder: boolean;
  rejectOrder: boolean;
  failedOrder: boolean;
  requestApproval: boolean;
  permitGranted: boolean;
  approvalSubmittedByCustomer: boolean;
  erectSigns: boolean;
  installationProtocol: boolean;
  sendDocumentsToCustomer: boolean;
  collectSigns: boolean;
  draftCreated: boolean;
  sendInvoice: boolean;
  invoiceReminded: boolean;
  invoicePaid: boolean;
  closeOrder: boolean;
};

export type InstallationStatusDateTime = {
  acceptOrderDateTime: string | null;
  rejectOrderDateTime: string | null;
  failedOrderDateTime: string | null;
  requestApprovalDateTime: string | null;
  permitGrantedDateTime: string | null;
  approvalSubmittedByCustomerDateTime: string | null;
  erectSignsDateTime: string | null;
  installationProtocolDateTime: string | null;
  sendDocumentsToCustomerDateTime: string | null;
  collectSignsDateTime: string | null;
  draftCreatedDateTime: string | null;
  sendInvoiceDateTime: string | null;
  invoiceRemindedDateTime: string | null;
  invoicePaidDateTime: string | null;
  closeOrderDateTime: string | null;
};

export type Customer = {
  id: string;
  createdAt: string;
  updatedAt: string;
  gender: Gender | null;
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  telephoneNumber: string;
  city?: string;
  zipCode?: string;
  cityAndZipCode: string;
  street?: string;
  houseNumber?: string;
  streetAndHouseNumber: string;
  notes: string;
};

export type Payment = {
  id: string;
  createdAt: string;
  updatedAt: string;
  details: {
    order: {
      amount: string;
    };
  };
  paymentId: string;
  paymentType: PaymentType;
  status: StausType;
};

export type MultiStatus = typeof MULTI_STATUS[number];

export type PriceTableSchema = {
  id: number;
  article: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  enabled: boolean;
  netPrice?: number;
};

export type UpdatedPrice = {
  totalPrice: number;
};

export type OrderModelForm = AdditionalInstallationInformation &
  LicensePlates &
  UpdatedPrice & {
    barrierDetailsCustomer: string;
    barrierDetailsHalteverbot24: string;
    barrierDetailsCity: string;
    barrierDetailsSetupDate: string;
    barrierDetailsSetupTime: string;
    barrierDetailsPickupDate: string;
    updatedDateRangeFrom: string;
    updatedDateRangeTo: string;
    notice: string;
  };

export type EventType = "setup" | "pickup";

export type ParkedCars = {
  id: string;
  carColor: string;
  licensePlate: string;
  brand: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
};

export enum PaymentType {
  BOOKED = "booked",
  PREPAYMENT = "prepayment",
  PAYED = "payed",
}

export enum StausType {
  PENDING = "pending",
  CONFIRMED = "confirmed",
}

export type UpdateInstallationRequestBodyDTO = Partial<{
  noSignInstallation: boolean;
  isImportant: boolean;
}>;

export type LexwareInvoiceType = {
  id: string;
  resourceUri: string;
  createdDate: string;
  updatedDate: string;
  version: number;
};
